<template>
  <tx-dialog
    v-model="visible"
    :title="t('digitalAssets.selectAccount')"
    width="450px"
    height="250px"
    :loading="loading"
    show-ok
    :ok-state="loading ? 'loading' : !sellerAccount || (sellerAccount === existingAccountId) ? 'disabled' : 'enabled'"
    confirm-text="general.save"
    @ok="doSave"
  >
    <div class="w-full h-full">
      <tx-alert :show="hasError" type="error" :text="errorMessage" dismissible />

      <div class="w-full">
        <tx-select
          v-model="sellerAccount"
          :label="t('digitalAssets.account')"
          required
          filterable
          :data="availableSellerAccounts"
          value-prop="AccountId"
          display-prop="AccountName"
        />
      </div>
    </div>
  </tx-dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { clone } from 'lodash-es'
import { getSellerAccounts } from '@/api/t1/sellers'
import type SellerDetails from '@/models/sellerDetails'
import TxDialog from '@/shared/components/TxDialog.vue'
import TxSelect from '@/shared/components/TxSelect.vue'
import { useUserStore } from '@/store/userData'
import TxAlert from '@/shared/components/TxAlert.vue'

const emit = defineEmits<{
  (e: 'accountSelected'): void
}>()

const { t } = useI18n()
const userStore = useUserStore()

const visible = ref(false)
const loading = ref(false)
const hasError = ref(false)
const errorMessage = ref('')
const sellerAccount = ref<number | null>(null)
const availableSellerAccounts = ref<SellerDetails[]>([])
const existingAccountId = ref<number | null>(null)

async function fetchSellerAccounts() {
  loading.value = true
  try {
    const res = await getSellerAccounts()
    availableSellerAccounts.value = res.data
      .filter(s => s.Status)
  }
  catch (err: any) {
    hasError.value = true
    errorMessage.value = err.message || 'Failed to load seller accounts'
  }
  finally {
    loading.value = false
  }
}

function showDialog(accountId?: number) {
  existingAccountId.value = accountId ? clone(accountId) : null
  sellerAccount.value = accountId ?? null
  fetchSellerAccounts()
  visible.value = true
}

async function doSave() {
  loading.value = true
  if (sellerAccount.value) {
    await userStore.setSellerData(sellerAccount.value)
    emit('accountSelected')
  }
  loading.value = false
  visible.value = false
}

defineExpose({ showDialog })
</script>
